import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { Pari } from '../../models/pari.model';
import { Race } from '../../models/race.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { Operator, Provider } from '../../models/provider.model';
import { PmuService } from '../../services/data/pmu.service';
import {
  BetPayOptions,
  SaveBetParam,
} from '../../models/save-bet-options.model';
import { Country } from '../../models/active-games-resp.model';
import { ConfigService } from '../../services/config/config.service';
import { BetslipService } from '../../services/betslip/betslip.service';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { buildPhoneRegex } from '../../functions/validation.function';
import { MatRippleModule } from '@angular/material/core';
import {
  operatorsRippleOptions,
  submitBtnRippleOptions,
} from '../../constants/ripple-options.constant';
import {
  formatPhoneNumber,
  formatSelectionString,
  sprintf,
} from '../../functions/format-strings.function';
import { Meeting } from '../../models/meeting.model';
import { GameFormula } from '../../models/game.model';
import {
  BetRespEif,
  BetSaveSuccessResp,
} from '../../models/status.payment.type';
import { PaymentStatus } from '../../../enums/status.payment.enum';
import {
  SaveBetErrorWrongRaceNo,
  SaveBetSuccess,
  SaveBetTransferError,
} from '../../sample-data/save-bet-resp.data';
import { BetStatusModalComponent } from '../interactive/bet-status-modal/bet-status-modal.component';
import { Router } from '@angular/router';
import {
  PaymentStatusSuccess,
  PaymentStatusFailed,
} from '../../sample-data/check-status-res.data';
import { filter, tap } from 'rxjs/operators';
import { TicketPreviewComponent } from '../interactive/ticket-preview/ticket-preview.component';
import { ActionBtn } from '../../models/overlay-loader.model';
import {MatDialog} from '@angular/material/dialog';
import { PrintService } from '../../services/print/print.service';
import { NotificationBoxComponent } from '../interactive/print-status/notification-box/notification-box.component';
import { WebsocketService } from '../../services/socket/websocket.service';
import { CurrencyFormatterPipe } from '../../pipes/currency-formatter/currency-formatter.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-betslip',
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatRippleModule,
    DatePipe,
    CurrencyFormatterPipe,
    NotificationBoxComponent
  ],
  templateUrl: './betslip.component.html',
  styleUrl: './betslip.component.scss',
})
export class BetslipComponent implements OnInit, OnChanges, OnDestroy {
  readonly dialog = inject(MatDialog);
  @Input() race?: Race;
  @Input() pari: Partial<Pari> = {};
  @Input() options: Partial<BetPayOptions> = {};
  @Input() meetings!: Meeting[];
  @Input() selectedFormula: GameFormula = 'SI';
  @Output() clearSelections = new EventEmitter<void>();
  @Output() formuleCompletChange = new EventEmitter<boolean>(false);
  @Input() isHorseSelected: boolean = false;

  selectedOperator!: Operator;
  selectedProvider!: Provider;
  selectedCountry!: Country;
  savebetParams!: Partial<SaveBetParam>;
  phoneErr: string = '';
  betSlipForm!: FormGroup;
  phoneLength: number = 0;
  startDigits: string [] = [];
  ticketData: string [] = [];

  notification: { type: 'success' | 'error', message: string } | null = null;
  notificationTimeout: any;

  operatorsRippleOptions = operatorsRippleOptions;
  submitBtnRippleOptions = submitBtnRippleOptions;
  errMsg: string = '';
  formattedPhoneNum!: string;
  paymentReference!: string;
  cfVal: boolean = false;

  subscription: Subscription = new Subscription();
  paymentTimeout!: any;

  get baseSelections() {
    return this.pari.chevalBases! || [];
  }

  get associateSelections() {
    return this.pari.chevalAssocies! || [];
  }

  get showFormulCompletChecker() {
    // return this.pari.game?.formulaControl;
    return this.pari.game?.hasOrder;
  }

  get formattedSelectionStr() {
    return formatSelectionString(
      this.baseSelections || [],
      this.associateSelections || [],
      this.pari.game?.baseHorseCount || 0,
      this.pari.race?.competitorsCount || 0,
      this.selectedFormula,
      this.pari.spotEnabled || false,
      this.pari.game?.spot
    );
  }

  constructor(
    private pmuService: PmuService,
    private configService: ConfigService,
    private betslipService: BetslipService,
    private translate: TranslateService,
    private viewCompRef: ViewContainerRef,
    private router: Router,
    private printService: PrintService,
    private websocketService: WebsocketService
  ) {
    if (!this.pari) {
      this.pari = pmuService.getCurrentData();
    }
  }
  get currency() {
    return this.configService.currency;
  }

  get stepNumber() {
    return 4;
  }

  get selectedHorseIDs() {
    return (
      this.pari.chevalBases?.map(
        (horse) => `${this.pari.race}-${this.pari.game?.code}-${horse}`
      ) ?? []
    );
  }

  get raceDistanceStr() {
    return `${this.race?.distance ?? 0} m`;
  }

  get horsesCountString() {
    return this.translate.instant('betslip.horses_count', {
      count: this.race?.competitorsCount ?? 0,
    });
  }

  get activeMeeting() {
    return this.meetings.find((m) => m.id === this.pari.meeting?.id);
  }

  get raceShortNameFormat() {
    return sprintf(
      this.configService.raceShortNameFormat || '',
      this.pari?.meeting?.id ?? 0,
      this.pari.race?.id ?? 0
    );
  }

  get raceFullNameFormat() {
    return sprintf(
      '%s-%s',
      this.activeMeeting?.name || '',
      this.pari?.race?.name || ''
    );
  }

  get validCombine() {
    const index = this.pari.sformula && ['CP', 'CE', 'CT'].includes(this.pari.sformula) ? 1 : 0;
    return this.pari.nCombine! > index;
  }

  get overLayOpts() {
    return this.pmuService.overLayOptions$.getValue();
  }

  get showCalculatedStakeWithoutTax() {
    return this.configService.taxOnStake && this.pari.montant != this.pari.payable;
  }
  /**
   * Clears the betslip.
   */
  deleteBet() {
    this.pari = {};
    this.cfVal = false;
    this.clearSelections.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']?.currentValue) {
      if (
        !this.selectedOperator &&
        changes['options'].currentValue?.operators?.length
      ) {
        this.setOperator(changes['options'].currentValue.operators[0]);
      }
      if (
        !this.selectedProvider &&
        changes['options'].currentValue?.providers?.length
      ) {
        this.setProvider(changes['options'].currentValue.providers[0]);
      }
      this.setCountry(changes['options'].currentValue.countries[0]);
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.savebetParams = {
      channel: 3,
      // userid: '',
      // pwd: '',
      ref: new Date().getTime().toString(),
      internal_ref: new Date().getTime().toString(),
      str_From: '',
      format: '',
      // mtv: 0,
      number: 0,
      str_Pick: '',
      ime: 0,
      // imei: 0,
      DT: 0,
    };
    this.subscription.add(
      this.websocketService.message$.subscribe(res => {
        clearTimeout(this.paymentTimeout);
        this.handlePaymentResponse(JSON.parse(res), this.overLayOpts);
      })
    );

    this.checkUnprintedTickets();

    this.subscription.add(
      this.printService.printingTicket$.pipe(filter((p) => !p)).subscribe((printing) => {
        if(!printing){
          this.checkUnprintedTickets();
        }
      })
    );
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    clearTimeout(this.paymentTimeout);
  }
  initForm() {
    this.betSlipForm = new FormGroup({
      phoneNumber: new FormControl('', [Validators.required]),
    });
    this.betSlipForm.get('phoneNumber')?.valueChanges.subscribe((change) => {
      this.onPhoneNumberChange(change);
    });
  }

  setOperator(operator: Operator) {
    const { mask } = operator;
    if (mask) {
      const [startRange, phoneLength] = mask.split('#');
      const startDigits = startRange.split('-');
    }
    this.selectedOperator = operator;
  }

  setProvider(operator: Provider) {
    const { mask } = operator;
    const [startRange, phoneLength] = mask.split('#');
    this.startDigits = startRange.split('-');
    this.phoneLength = Number(phoneLength);
    this.selectedProvider = operator;
    if(this.betSlipForm?.get('phoneNumber')?.dirty){
      this.onPhoneNumberChange(this.betSlipForm.get('phoneNumber')?.value);
    }
  }

  setCountry(country: Country) {
    this.selectedCountry = country;
  }

  operatorIsSelected(operator: Operator) {
    return this.selectedOperator?.code === operator.code;
  }
  providerIsSelected(operator: Provider) {
    return this.selectedProvider?.id === operator.id;
  }

  supprimerPari() {
    this.pari = null as any;
  }

  toggleCompleteFormula() {
    const cf = this.cfVal;
    this.pmuService.updateData({...this.pmuService.getCurrentData(), completeFormula: cf});
    this.formuleCompletChange.emit(cf);
  }

  validateBet() {
    if (this.betSlipForm.invalid) {
      return;
    }
    const hasMulti = ['MULTIC3', 'PM', 'MM', 'PMM', 'MULTI'].includes(
      this.pari.game?.code || ''
    ) ? '1' : '0';

    const {completeFormula, multi} = this.pari;
    const compl = 0;  // cheval complement
    const { code, hasOrder, flexi, spot, baseHorseCount } = this.pari.game || {};
    const { coefficient, chevalBases: baseSelections, chevalAssocies: associateSelections, race, mise, montant, payable, spotEnabled } = this.pari;

    let selection = (baseSelections || []).concat((associateSelections || []));

    const baseCode = !this.selectedHorseIDs.length
      ? code
      : code?.split('_')[0] || '';

    let format!: string;
    const { betFormatVersion } = this.configService;

    switch (betFormatVersion) {
      case '4.0':
        format = `${baseCode}-${completeFormula ? 1 : 0}-${compl}-${coefficient}-${flexi}-${
          (spot?.enabled && spotEnabled) ? '1' : '0'
        }-${selection?.join('-')}`;
        break;
      case '4.3':
        format = `${baseCode}-${completeFormula ? 1 : 0}-${compl}-${coefficient}-${flexi}-${
          (spot?.enabled && spotEnabled) ? '1' : '0'
        }-${hasMulti ? (multi || 0) : 0}-${selection?.join('-')}`;
    }

    const { imei, DT, mode, pwd, userid, channel } =
      this.options?.deviceInfo ?? {};
    const { phoneNumber } = this.betSlipForm.value;

    this.savebetParams = {
      ...this.savebetParams,
      number: race?.numero,
      format: format.replace(/X/g, '*'),
      str_From: phoneNumber.replace(/ /g, ''),
      // mtv: baseHorseCount,
      ref: new Date().getTime().toString(),
      internal_ref: new Date().getTime().toString(),
      str_Pick: selection?.join('-'),
      ime: imei,
      imei,
      DT,
      mode,
      // pwd,
      // userid,

      channel,
      amount: payable,
      check_multi: Boolean(JSON.parse(hasMulti)),
      intCoef: coefficient,
      pvd: +this.selectedProvider.id,
      option: baseCode,
      lang: this.translate.currentLang.split('-')[0]
    };

    this.pmuService.overLayLoader$.next(true);
    this.pmuService.overLayOptions$.next({
      msg: this.translate.instant('bet_status.processing'),
      spinner: true,
    });
    this.betslipService
      .saveAnonymousBet(this.savebetParams)
      .pipe()
      .subscribe(
        (data: any) => {
          let betEif!: Partial<BetRespEif> | string;
          if (data.length) {
            const betResponse = data[0];
            // const betResponse: any = SaveBetTransferError[0];
            if ('vld' in betResponse) {
              if (!Boolean(JSON.parse(betResponse['vld']))) {
                // betEif = betResponse['eif'];
                const errMsg = this.formatErrMsg(
                  betResponse['msg'] ?? betResponse['eif'] ??  ""
                );
                this.hideOverLay();
                this.openBetErrorModal(errMsg);
              } else {
                if('eif' in betResponse) {
                  betEif = JSON.parse(betResponse['eif']);
                }
                if ('paymentStatus' in betResponse) {
                  switch (betResponse['paymentStatus'] as PaymentStatus) {
                    case PaymentStatus.PENDING:
                      this.formattedPhoneNum = formatPhoneNumber(
                        this.selectedCountry?.id,
                        phoneNumber
                      );
                      this.showPaymentPendingOverlay.call(this);
                      this.handleMobilePayment({
                        ...betResponse,
                        vld: Boolean(JSON.parse(betResponse['vld'])),
                        eif: betEif,
                      });
                      break;

                    default:
                      break;
                  }
                }
              }
            }
          }
        },
        (error) => {
          console.log('Error saving bet:', error);
          this.pmuService.overLayLoader$.next(false);
          this.pmuService.overLayOptions$.complete();
        },
        () => {
        }
      );
  }

  formatErrMsg(msgKey: string) {
    return this.translate.instant(msgKey);
  }


  handleMobilePayment(betPlaceResp: BetSaveSuccessResp) {
    const { recursive, checkStatus, initial } = this.configService.betPaymentWait;
    const paymentReference = betPlaceResp.paymentRefId
      ? betPlaceResp.paymentRefId
      : betPlaceResp.eif?.traceID;
    this.paymentReference = paymentReference;

    this.paymentTimeout = setTimeout(() => {
      clearTimeout(this.paymentTimeout);
      this.showPaymentProcessingOverlay.call(this);
    }, checkStatus);

    this.websocketService.connect(paymentReference);
  }

  checkPaymentStatus() {
    const params = { traceId: this.paymentReference };
    return this.betslipService.checkStatus(params).pipe(
      tap((response: any) => {
        this.handlePaymentResponse(response, this.overLayOpts);
      })
    );
  }

  private handlePaymentResponse(response: any, overLayOpts: any): boolean {
    // response = PaymentStatusFailed;
    const status = response?.paymentStatus;

    switch (status) {
      case PaymentStatus.SUCCESS:
        if(response.data){
          this.showPaymentSuccessOverlay.call(this, response.data);
        }else {
          this.showPaymentFailedOverlay.call(this);
        }
        return false;
      case PaymentStatus.FAILED:
        this.showPaymentFailedOverlay.call(this);
        return false;
      case PaymentStatus.PENDING:
        this.showPaymentProcessingOverlay.call(this);
        return true;
      default:
        return false;
    }
  }

  private showPaymentPendingOverlay() {
    this.pmuService.overLayOptions$.next({
      ...this.overLayOpts,
      spinner: true,
      msg: this.translate.instant('bet_status.payment_pending', {
        phone: this.formattedPhoneNum,
      }),
      actions: [
        {
          label: 'bet_action.cancel',
          action: () => (this.hideOverLay(), this.websocketService.disconnect()),
          className: 'btn-danger',
        },
      ],
    });
  }

  private showPaymentSuccessOverlay(successResp: any) {

    this.pmuService.overLayOptions$.next({
      ...this.overLayOpts,
      spinner: true,
      msg: this.translate.instant(`bet_status.success`),
      actions: [
        // {
        //   label: 'bet_action.cancel',
        //   action: () => (this.goHome(), this.websocketService.disconnect()),
        //   className: 'btn-primary',
        // },
        // {
        //   label: 'bet_action.preview_ticket',
        //   action: () => this.showTicketPreview(successResp),
        //   className: 'btn-success',
        // }
      ],
    });
    if(Array.isArray(successResp) && successResp.length){
      this.ticketData = successResp;
      this.printTicket();
    }
  }

  private showPaymentFailedOverlay() {
    this.pmuService.overLayOptions$.next({
      ...this.overLayOpts,
      spinner: false,
      msg: this.translate.instant(`bet_status.failed`),
      actions: [
        {
          label: 'bet_action.try_again',
          action: () => this.validateBet(),
          className: 'btn-secondary',
        },
        {
          label: 'bet_action.return',
          action: () => this.goHome(),
          className: 'btn-primary',
        },
      ],
    });
  }

  private showPaymentProcessingOverlay() {
    this.pmuService.overLayOptions$.next({
      ...this.overLayOpts,
      spinner: true,
      msg: this.translate.instant('bet_status.pending', {
        phone: this.formattedPhoneNum
      }),
      actions: [
        {
          label: 'bet_action.check_status',
          action: () => {
            this.checkPaymentStatus().subscribe();
          },
          className: 'btn-info',
        }
      ],
    });
  }

  openBetErrorModal(msg: string) {
    const dialogRef = this.dialog.open(BetStatusModalComponent, {
      height: '200px', width: '230px',
      data: {title: "bet_status.title_error", msg},
    });
    dialogRef.afterClosed().subscribe(result => {
    });
  }

  showTicketPreview(ticket: string[]) {
    this.ticketData = ticket;
    this.viewCompRef.remove();
    this.hideOverLay();
    const componentRef = this.viewCompRef.createComponent(
      TicketPreviewComponent
    );
    const ticketActions: ActionBtn[] = [
      {
        label: 'ticket_action.print_ticket',
        className: 'btn-success',
        action: () => this.printTicket()
      },
      {
        label: 'ticket_action.play_again',
        className: 'btn-info',
        action: () => (this.deleteBet(), this.viewCompRef.remove())
      },
      {
        label: 'ticket_action.end_session',
        className: 'btn-danger',
        action: () => this.goHome()
      }
    ]
    componentRef.setInput('ticketData', ticket.slice(3));
    componentRef.setInput('pari', this.pari);
    componentRef.setInput('ticketActions', ticketActions);
    componentRef.instance.closeModal.subscribe((res) => {
      if (res == true) {
        this.viewCompRef.remove();
      }
    });
  }

  printTicket(): void {
    const lines = this.ticketData || [];
    this.pmuService.overLayOptions$.next({
      ...this.overLayOpts,
      spinner: true,
      msg: "ticket_status.printing",
      actions: []
    })
    this.printService.printTicket(lines).then((response) => {
      let statusText: 'success' | 'error' = response.status ? 'success' : 'error';
      this.showNotification(statusText, response.msg);
      if(response.status){
        setTimeout(() => {
          this.goHome();
        }, 5500);
      }
    }).catch((err) => {
      this.showNotification('error', 'print_status.printing_failed');
    }).finally(() => {
      if(this.websocketService.connected){
        this.websocketService.disconnect();
      }
    });
  }

  showNotification(type: 'success' | 'error', message: string) {
    this.notification = { type, message };
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout);
    this.notificationTimeout = setTimeout(() => this.notification = null, 5000);
  }
  closeNotification() {
    this.notification = null;
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout);
  }


  getOperatorIconName(operator: Operator) {
    return `mobile_${operator.libelle.split(' ').join('_').toUpperCase()}.png`;
  }

  getProviderIconName(provider: Provider) {
    return `mobile_${provider.desc.split(' ').join('_').toUpperCase()}.png`;
  }

  isSelectedCountry(_country: Country) {
    return _country.id == this.selectedCountry?.id;
  }

  onCountrySelected(_evt: Event) {
    console.log('country changed', _evt);
  }

  get phoneRegex() {
    return buildPhoneRegex(this.selectedCountry?.id ?? "");
  }

  onPhoneNumberChange(_num: string | null) {
    this.phoneErr = '';
    if (_num == null || ((typeof(_num) == 'string') && (_num.length !== this.phoneLength || this.startDigits.every(prefix => !_num.startsWith(prefix))))) {
      this.phoneErr = 'form_err.phone_invalid';
      // this.betSlipForm.get('phoneNumber')?.setErrors({invalid: true})
    }
  }

  disableSubmit() {
    return this.phoneErr.length > 0 || !this.betSlipForm.valid || !this.isHorseSelected || !this.validCombine;
  }

  goHome(): void {
    this.router.navigate(['list-games']).then((res) => {
      this.hideOverLay();
      this.deleteBet();
      this.websocketService.disconnect();
      this.pmuService.refreshAppState.next(true);
    });
  }

  checkUnprintedTickets(){
      let data: string | null = localStorage.getItem('currentTicket');
      if(data !== null){
        const { lines } = JSON.parse(data);
        if(Array.isArray(lines) && lines.length){
          this.ticketData = lines;
          this.pmuService.overLayLoader$.next(true);
          this.pmuService.overLayOptions$.next({
            spinner: false,
            msg: "ticket_status.unprinted",
            actions: [
              {
                label: 'ticket_action.print_ticket',
                action: () => this.printTicket(),
                className: 'btn-success',
              }
            ],
          })
        }
      };
  }

  hideOverLay() {
    this.paymentReference = "";
    this.pmuService.overLayLoader$.next(false);
  }
}
